import { useColorScheme } from '@/hooks/useColorScheme'
import { HeadingLabel, MetaDataLabel } from '@/styles/fonts'
import { GrandstandIcon } from '@grandstand-web/bally-web-shared/src/components/GrandstandIcon'
import { useLocalStorage } from '@grandstand-web/bally-web-shared/src/hooks/useLocalStorage'
import { ApiClient } from '@grandstand-web/bally-web-shared/src/newPackages/ApiClient'
import { S } from '@grandstand-web/bally-web-shared/src/newPackages/StringMgmt'
import { AnalyticServiceContext } from '@grandstand-web/bally-web-shared/src/services/analytics/AnalyticService'
import {
  ConfigServiceContext,
  useContextUnconditionally,
} from '@grandstand-web/bally-web-shared/src/services/config/ConfigService'
import { useTealium } from '@grandstand-web/bally-web-shared/src/services/tealium/TealiumService'
import { UserServiceContext } from '@grandstand-web/bally-web-shared/src/services/user/UserService'
import { delay } from '@grandstand-web/bally-web-shared/src/utils/queryUtils'
import { ISO8601ToMonthDateFullYear } from '@grandstand-web/bally-web-shared/src/utils/stringUtils'
import {
  ActiveSubscriptionsResponse,
  FinalBillResponse,
  LogoLeagueObjV2,
  UpgradeDowngradeSubscriptionResponse,
} from '@grandstand/presentation-models'
import Image from 'next/image'
import { SyntheticEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { GrandstandButton } from '../Buttons'
import { LoadingSpinner } from '../loading'
import Modal from '../modals/PopupModal'
import SubscriptionManager from './SubscriptionManager'

type SubscriptionTileProps = {
  subscription: ActiveSubscriptionsResponse[0]
  ccLast4Digits?: string | null
}

const TileContainer = styled.div`
  padding: 25px;
`

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  background-color: var(--canvas);
  color: var(--on-background);
  padding-bottom: 30px;
`

const TopBar = styled.div`
  font-weight: bold;
  font-size: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 20px;
  top: 0;
  left: 0;
  border-bottom: 1px solid var(--divider-canvas);
`
const RegionLogo = styled.img`
  width: 162px;
  margin-top: 24px;
`

const LogoRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 30px;
  margin-top: 20px;
`

const LogoImageWrapper = styled.div`
  border-radius: 50%;
  border-color: var(--canvas);
  border-width: 2px;
  border-style: solid;
  padding: 24px;
  background-color: var(--surface);
  position: relative;
`

const LogoImage = styled(Image)`
  img {
    margin-left: 40px;
    border-radius: 50%;
    background-color: var(--surface);
    @media (max-width: 1042px) {
      display: none;
    }
  }
`

const TopContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 16px;
  height: 200px;
  margin-bottom: 25px;
`

const FieldDiv = styled.div`
  padding-left: 25px;
  padding-right: 25px;
  div {
    position: relative;
  }
`

const ModalDl = styled.dl`
  display: flex;
  justify-content: space-between;
  width: 100%;
  flex-wrap: wrap;
`
const ModalDt = styled.dt`
  width: 50%;
  text-align: left;
`
const ModalDd = styled.dd`
  width: 50%;
  text-align: right;
`

const FieldLabel = styled(MetaDataLabel)`
  color: var(--on-background-alt);
`
const FieldPromoLabel = styled(FieldLabel)`
  color: var(--success);
`

const FieldFooter = styled(FieldLabel)`
  text-align: center;
  border-top: 1px solid var(--divider-canvas);
  margin-top: 30px;
  @media (min-width: 800px) {
    height: 30px;
  }
  div {
    margin-top: 20px;
  }
`

const Notify = styled(FieldLabel)`
  padding: 30px;
`

const InnerNotify = styled.div`
  border-radius: 10px;
  display: flex;
  color: initial;
  justify-content: center;
  align-items: center;
  background: rgba(0, 73, 146, 1);
  padding: 30px 30px 30px 10px;
  a {
    text-decoration: underline;
  }
  div {
    display: inline-block;
    margin-left: 10px;
    margin-right: 10px;
    margin-top: -2px;
  }
  div:first-child {
    flex-shrink: 0;
  }
`

const ErrorIcon = styled(GrandstandIcon)``

const FieldInfo = styled(MetaDataLabel)`
  float: right;
  top: -20px;
`

const FieldButtonRow = styled(FieldInfo)`
  @media (max-width: 1042px) {
    text-align: center;
    button {
      margin-right: 0;
      float: none;
      width: 90%;
      margin-bottom: 10px;
    }
  }
`

const FieldEdit = styled(GrandstandButton)`
  margin-right: 25px;
  float: right;
  background-color: var(--surface);
  border: 1px solid var(--surface-light);
  color: var(--surface-light);
  :hover {
    background-color: var(--surface);
    border: 1px solid var(--surface-light);
    color: var(--surface-light);
  }
`

const FieldEditLight = styled(FieldEdit)`
  color: var(--on-light);
  :hover {
    color: var(--on-light);
  }
`

const ModalHeading = styled(HeadingLabel)`
  color: var(--on-background);
`

enum PaymentMethods {
  Card = 'Credit/Debit Card',
  PayPal = 'PayPal Account',
  Stripe = 'Stripe',
  Google = 'Google Pay',
  Apple = 'Apple Pay',
  Credit = 'Credit/Debit Card',
}

const renderSecondRowTitle = (status: string) => {
  let text
  switch (status) {
    case 'active':
    case 'paused':
      text = 'Next billing date'
      break
    case 'expired':
      text = 'Expired'
      break
    case 'canceled':
      text = 'Subscription expires on'
      break
    default:
      text = 'Next billing date'
  }
  return text
}

const renderSecondRowInfo = (subscription: ActiveSubscriptionsResponse[number]) => {
  const formatDate = (date: string): string => {
    return new Date(date).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    })
  }
  let data
  switch (subscription.subscription_status) {
    case 'expired':
      data = formatDate(subscription.valid_until)
      break
    default:
      data =
        subscription.dtc_product_type === 'package-seasonal'
          ? 'Next Season Start'
          : formatDate(subscription.next_bill_date)
  }
  return data || 'N/A'
}

const renderThirdRowTitle = (status: string) => {
  let text
  switch (status) {
    case 'paused':
      text = 'Subscription paused until'
      break
    default:
      text = 'Payment Method'
  }
  return text
}

const renderThirdRowInfo = (subscription: ActiveSubscriptionsResponse[number]) => {
  let data
  switch (subscription.subscription_status) {
    case 'paused':
      data = subscription.pause_expiration
      break
    default:
      data = subscription.payment_method
  }
  return data
}

const renderLogos = (teamImages: LogoLeagueObjV2[], isDark: boolean) => {
  return teamImages.map((item, index) => {
    const logoImg = isDark ? item.dark_logo : item.light_logo
    return (
      <LogoImageWrapper key={index}>
        <LogoImage src={logoImg} alt={`Logo ${item.team}`} width={32} height={32} />
      </LogoImageWrapper>
    )
  })
}

const SubscriptionTile = ({ subscription, ccLast4Digits }: SubscriptionTileProps) => {
  const [managementAction, setManagementAction] = useState(false)
  const [showModal, setShowModal] = useState<'none' | 'upgrade' | 'error' | 'success'>('none')
  const [upgradePriceResult, setUpgradePriceResult] = useState<number | null>(null)
  const [annualSubscription, setAnnualSubscription] = useState<FinalBillResponse['order_details'][number] | null>(null)
  const [pauseCancelErrorMessage, setPauseCancelErrorMessage] = useState('')
  const handleCloseModal = () => setShowModal('none')
  const { currentConfig } = useContextUnconditionally(ConfigServiceContext)
  const userService = useContext(UserServiceContext)
  const analyticService = useContext(AnalyticServiceContext)
  const [_, setCaptivateKey] = useLocalStorage('captivateKey', '')
  const manageForm = useRef<HTMLFormElement>(null)
  const accessTokenInput = useRef<HTMLInputElement>(null)
  const captivateKeyInput = useRef<HTMLInputElement>(null)
  const buttonTypeInput = useRef<HTMLInputElement>(null)

  const teamImages = useMemo(() => subscription.logos, [subscription.logos])
  const payPalPaymentMethod = useMemo(
    () => subscription.payment_method === PaymentMethods.PayPal,
    [subscription.payment_method]
  )
  const manageWithWeb = useMemo(() => subscription.source === 'web', [subscription.source])
  const [upgradeConfirmed, setUpgradeConfirmed] = useState(false)
  const handleManagementSubmit = async (action: string, e: SyntheticEvent) => {
    e.preventDefault()
    if (accessTokenInput.current && captivateKeyInput.current && manageForm.current && buttonTypeInput.current) {
      const url = currentConfig.API.services.subscription_services.captivate.entrypoint
      const response = await ApiClient.convenientApiFetch({
        url: url,
        method: 'POST',
        body: {
          action: action,
          service_id: subscription.service_id,
        },
      })
      const data = await response.json()
      if (!response.ok) {
        setPauseCancelErrorMessage(data.message)
        return
      }
      setCaptivateKey(data.key)
      captivateKeyInput.current.value = data.key
      accessTokenInput.current.value = data.token
      buttonTypeInput.current.value = action
      manageForm.current.action = data.url
      manageForm.current.submit()
    }
  }

  // Presently we do not have any requirement to allow users to select from a variety of upgrade options.
  // While subscription.upgrade_options is an array we only expect it to only ever contain one item, and
  // that item will always be an annual package.
  const annualServiceId = subscription.upgrade_options.at(0)
  const { isDark } = useColorScheme()
  const tealium = useTealium()

  useEffect(() => {
    const getUpgradeBill = async () => {
      if (!userService.isLoggedIn) {
        return
      }

      // never fetch upgrade price for annual subscriptions
      if (subscription.billing_interval === 'annual') {
        return
      }

      const url = currentConfig.API.services.subscription_services.bill

      try {
        const response = await ApiClient.convenientApiFetch({
          url: url,
          method: 'POST',
          body: {
            selected_packages: [
              {
                service_type: 'PACKAGE',
                service_id: annualServiceId?.service_id,
                quantity: 1,
              },
            ],
            coupons: [],
            address: {
              zip_code: subscription.service_zipcode,
            },
          },
        })

        const data = await response.json()
        const annualBill = data as FinalBillResponse
        const annualSubscriptionPrice = annualBill.total_price_after_tax
        setUpgradePriceResult(annualSubscriptionPrice)
        setAnnualSubscription(annualBill.order_details[0])
      } catch (error) {
        console.error('Error fetching upgrade price:', error)
        setUpgradePriceResult(null)
        setAnnualSubscription(null)
      }
    }

    getUpgradeBill()
  }, [
    currentConfig,
    subscription.service_id,
    subscription.service_zipcode,
    subscription.upgrade_options,
    userService.isLoggedIn,
    annualServiceId,
    subscription.billing_interval,
  ])

  const upgradeSubscription = useCallback(async () => {
    if (!userService.isLoggedIn || !annualServiceId) {
      return
    }

    const url = currentConfig.API.services.subscription_services.upgrade

    return ApiClient.convenientApiFetch({
      url: url,
      method: 'PUT',
      body: {
        order_id: subscription.order_id,
        old_service_id: subscription.service_id,
        new_service_id: annualServiceId.service_id,
        change_type: 'upgrade',
      },
    })
  }, [currentConfig, subscription.order_id, subscription.service_id, userService.isLoggedIn, annualServiceId])

  const handleShowConfirmationModal = async () => {
    if (!upgradeConfirmed) {
      setUpgradeConfirmed(true)
      const result = await upgradeSubscription()
      if (result?.ok) {
        analyticService.upgradeAnnualSubscription()
        const data = (await result.json()) as UpgradeDowngradeSubscriptionResponse
        tealium.upgradeToAnnual({
          package_code: data.service_id,
          package: annualSubscription ? annualSubscription.service_name : subscription.service_name,
          package_duration: 'annual',
        })

        setShowModal('success')
      } else {
        const resp = await result?.json()
        if (resp.message.startsWith('Upstream Evergent API failed to respond within')) {
          // Since there is no way for client to recover from this early timeout from middleware,
          // we give them some extra time to hopefully complete the transaction and assume it succeeded.
          // The page will reload after user confirms success if the status does not show the updated state
          // then we expect the issue to be reported to customer care.
          await delay(5000)
          setShowModal('success')
        } else {
          setShowModal('error')
        }
      }
    }
  }

  const CaptivateForm = () => {
    return (
      <form action="" method="post" ref={manageForm}>
        <input type="hidden" name="buttonType" value="" ref={buttonTypeInput} />
        <input type="hidden" name="accessToken" value="" ref={accessTokenInput} />
        <input type="hidden" name="dmaID" value="001" />
        <input type="hidden" name="locale" value="en_US" />
        <input type="hidden" name="skuID" value={subscription.service_id} />
        <input type="hidden" name="key" value="" ref={captivateKeyInput} />
      </form>
    )
  }

  const renderButtonRow = (status: string) => {
    if (!manageWithWeb) {
      return null
    }
    // Neither seasonal packages nor free trials can be paused
    let canPause = subscription.dtc_product_type !== 'package-seasonal'
    if (subscription.free_trial_expiration) {
      canPause = new Date(subscription.free_trial_expiration).valueOf() < Date.now().valueOf()
    }
    switch (status) {
      case 'active':
      case 'paused':
      case 'seasonal_paused':
        return (
          <div>
            {subscription.upgrade_options.length > 0 && subscription.management_enabled && (
              <div>
                <FieldEditLight
                  onClick={() => {
                    setShowModal('upgrade')
                  }}
                  data-theme="light"
                  buttonStyle="primary"
                  buttonSize="sm"
                >
                  UPGRADE TO ANNUAL
                </FieldEditLight>
              </div>
            )}
            {/* There is an edge case for downgraded annual packages. They will remain to appear exactly the same as any other active annual package, but we should not allow users to take any management action on them. This is the only scenario where an unexpired subscription will have subscription.management_enabled = false. */}
            {status === 'paused' && subscription.management_enabled && (
              <div>
                <FieldEdit
                  data-theme="dark"
                  buttonStyle="primary"
                  buttonSize="sm"
                  onClick={() => {
                    setManagementAction(true)
                  }}
                >
                  MANAGE SUBSCRIPTION
                </FieldEdit>
              </div>
            )}
            {(status === 'active' || status === 'seasonal_paused') && subscription.management_enabled && (
              <div>
                {canPause ? (
                  <FieldEdit
                    onClick={(e) => handleManagementSubmit('pause', e)}
                    data-theme="dark"
                    buttonStyle="primary"
                    buttonSize="sm"
                  >
                    PAUSE
                  </FieldEdit>
                ) : null}
                <FieldEdit
                  onClick={(e) => handleManagementSubmit('cancel', e)}
                  data-theme="dark"
                  buttonStyle="primary"
                  buttonSize="sm"
                >
                  CANCEL
                </FieldEdit>
              </div>
            )}
          </div>
        )
      case 'canceled':
        return (
          <div>
            {subscription.management_enabled && (
              <div>
                <FieldEditLight
                  data-theme="light"
                  buttonStyle="primary"
                  buttonSize="sm"
                  onClick={() => {
                    setManagementAction(true)
                  }}
                >
                  RENEW SUBSCRIPTION
                </FieldEditLight>
              </div>
            )}
          </div>
        )

      default:
        return <div></div>
    }
  }

  const renderUpgradeModal = () => {
    return (
      <Modal show={showModal !== 'none'} onClose={() => setShowModal('none')}>
        <TopBar>
          <Modal.Title>You will be upgraded to the {annualSubscription?.display_name} package.</Modal.Title>
        </TopBar>
        <Modal.Subtitle fontSize="1" mobileFontSize="1">
          Use your current payment info and upgrade your subscription with immediate access. Your billing details will
          update to the following:
        </Modal.Subtitle>
        <ModalDl>
          <ModalDt>Total: </ModalDt>
          <ModalDd>${upgradePriceResult}</ModalDd>
          <ModalDt>
            {payPalPaymentMethod && 'Payment Method:'}
            {!payPalPaymentMethod && 'Credit Card:'}
          </ModalDt>
          <ModalDd>
            {payPalPaymentMethod && 'Paypal'}
            {!payPalPaymentMethod && '****' + ccLast4Digits?.slice(-4)}
          </ModalDd>
          <ModalDt>Billing Date:</ModalDt>
          <ModalDd>{ISO8601ToMonthDateFullYear(subscription.next_bill_date)}</ModalDd>
        </ModalDl>

        {upgradeConfirmed ? (
          <>
            <LoadingSpinner />
          </>
        ) : (
          <>
            {' '}
            <Modal.Button onClick={handleShowConfirmationModal} buttonStyle="primary">
              Accept &amp; Upgrade
            </Modal.Button>
            <Modal.Button
              buttonStyle="alt"
              onClick={() => {
                setShowModal('none')
              }}
            >
              Cancel
            </Modal.Button>
          </>
        )}
      </Modal>
    )
  }

  const renderConfirmedModal = () => {
    const handleConfirmationOK = () => {
      setShowModal('none')
      window.location.reload()
    }

    return (
      <Modal show={showModal !== 'none'} onClose={handleConfirmationOK}>
        <Modal.Title fontSize="1">Thanks for upgrading.</Modal.Title>
        <Modal.Subtitle fontSize="1">
          Your subscription has been successfully upgraded to the annual plan. Check your email for details.
        </Modal.Subtitle>
        <Modal.Button buttonStyle="primary" onClick={handleConfirmationOK}>
          OK
        </Modal.Button>
      </Modal>
    )
  }

  const renderErrorModal = () => {
    return (
      <Modal show={showModal !== 'none'} onClose={handleCloseModal}>
        <Modal.Title fontSize="1">Error Upgrading!</Modal.Title>
        <Modal.Subtitle fontSize="1">
          There was an error updating this subscription, please try again or contact customer service.
        </Modal.Subtitle>
        <Modal.Button buttonStyle="primary" onClick={handleCloseModal}>
          OK
        </Modal.Button>
      </Modal>
    )
  }

  const renderPauseCancelErrorModal = () => {
    return (
      <Modal show={!!pauseCancelErrorMessage} onClose={() => setPauseCancelErrorMessage('')}>
        <Modal.Title fontSize="1">Error!</Modal.Title>
        <Modal.Subtitle fontSize="1">{pauseCancelErrorMessage}</Modal.Subtitle>
        <Modal.Button buttonStyle="primary" onClick={() => setPauseCancelErrorMessage('')}>
          OK
        </Modal.Button>
      </Modal>
    )
  }

  return (
    <>
      {showModal === 'upgrade' && renderUpgradeModal()}
      {showModal === 'success' && renderConfirmedModal()}
      {showModal === 'error' && renderErrorModal()}
      {renderPauseCancelErrorModal()}
      <CaptivateForm />
      <TileContainer data-theme="dark">
        <MainContent>
          <TopContainer>
            <TopBar>
              {subscription.service_name}
              <LogoRow>{renderLogos(teamImages, isDark)}</LogoRow>
            </TopBar>
          </TopContainer>
          <FieldDiv>
            <FieldLabel fontSize="sm">{subscription.service_name} subscription</FieldLabel>
            {subscription.free_trial_expiration && (
              <FieldPromoLabel fontSize="xs">
                *Free trial applied until {ISO8601ToMonthDateFullYear(subscription.free_trial_expiration)}
                <br />
                {/* Client product team agreed to the message below, which differs from the Figma designs, for launch due to difficulties with being able to determine variable mid-season pricing */}
                Discounts applied until {ISO8601ToMonthDateFullYear(subscription.valid_until)}
              </FieldPromoLabel>
            )}
            <FieldInfo fontSize="sm">${subscription.purchase_price}</FieldInfo>
          </FieldDiv>
          <FieldDiv>
            <FieldLabel fontSize="sm">{renderSecondRowTitle(subscription.subscription_status)}</FieldLabel>
            <FieldInfo fontSize="sm">{renderSecondRowInfo(subscription)}</FieldInfo>
          </FieldDiv>
          {subscription.subscription_status === 'paused' ||
            (subscription.subscription_status === 'expired' && (
              <FieldDiv>
                <FieldLabel fontSize="sm">{renderThirdRowTitle(subscription.subscription_status)}</FieldLabel>
                <FieldInfo fontSize="sm">{renderThirdRowInfo(subscription)}</FieldInfo>
              </FieldDiv>
            ))}
          {subscription.subscription_status === 'seasonal_paused' && (
            <Notify fontSize="md">
              <InnerNotify>
                <ErrorIcon icon="error" />
                <S stringKey="seasonal.pause.subscription.card.explanation.html" />
              </InnerNotify>
            </Notify>
          )}
          {subscription.subscription_status === 'active' && (
            <FieldButtonRow fontSize="sm">{renderButtonRow(subscription.subscription_status)}</FieldButtonRow>
          )}

          {subscription.subscription_status === 'canceled' && (
            <FieldButtonRow fontSize="sm">{renderButtonRow(subscription.subscription_status)}</FieldButtonRow>
          )}

          {/paused/.test(subscription.subscription_status) && (
            <FieldButtonRow fontSize="sm">{renderButtonRow(subscription.subscription_status)}</FieldButtonRow>
          )}
          {subscription.disclaimer && (
            <FieldFooter fontSize="xs">
              <div>{subscription.disclaimer}</div>
            </FieldFooter>
          )}
          {!manageWithWeb && (
            <Notify fontSize="md">
              <InnerNotify>
                <ErrorIcon icon="error" />
                If you want to resubscribe to this package, you can resubscribe via {subscription.payment_method}.
              </InnerNotify>
            </Notify>
          )}
        </MainContent>
        {managementAction && (
          <SubscriptionManager subscription={subscription} onDismiss={() => setManagementAction(false)} />
        )}
      </TileContainer>
    </>
  )
}
export default SubscriptionTile
